import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, authedFetch } from '../hooks/useAuth';

type EnrollResponse = { qr_svg: string; b32_key: string };

export default function TwoFAEnroll() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [enroll, setEnroll] = useState<EnrollResponse | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (loading || !user) return;
    if (user.totp_enabled) { nav('/profile'); return; }
    authedFetch('/api/v1/auth/2fa/enroll', { method: 'POST' })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setEnroll(d));
  }, [user, loading]);

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault();
    if (!enroll) return;
    setError('');
    setSaving(true);
    try {
      // Only send the TOTP code — secret is stored server-side from the enroll step
      const r = await authedFetch('/api/v1/auth/2fa/enroll-confirm', {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      const data = await r.json();
      if (!r.ok) { setError(data.detail ?? 'Invalid code.'); return; }
      await refresh();
      setDone(true);
    } catch {
      setError('Network error.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return null;

  if (!user) {
    nav('/login');
    return null;
  }

  if (done) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
        <div className="rounded-2xl border border-green-200 bg-white p-8 text-center shadow-lg">
          <p className="text-xl font-bold text-green-700">2FA enabled!</p>
          <p className="mt-2 text-sm text-slate-500">Your account is now protected with two-factor authentication.</p>
          <Link to="/profile" className="mt-4 inline-block rounded-full bg-sky-600 px-6 py-2 font-semibold text-white">
            Back to profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-sky-50 to-indigo-50 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-lg">
        <Link to="/profile" className="mb-6 block text-xs text-slate-400 hover:text-slate-600">← Profile</Link>
        <h1 className="mb-1 text-2xl font-bold text-slate-900">Enable 2FA</h1>
        <p className="mb-4 text-sm text-slate-500">
          Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.), then enter the code to confirm.
        </p>

        {enroll ? (
          <>
            {/* Render QR as an opaque image to prevent SVG script injection */}
            <div className="mx-auto mb-4 flex w-48 items-center justify-center rounded-xl border border-slate-200 bg-white p-2">
              <img
                src={`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(enroll.qr_svg)))}`}
                alt="TOTP QR code — scan with your authenticator app"
                className="w-full"
              />
            </div>
            <p className="mb-4 text-center text-xs text-slate-400 break-all">
              Manual key: <span className="font-mono font-semibold text-slate-700">{enroll.b32_key}</span>
            </p>
            <form onSubmit={handleConfirm} className="space-y-4">
              <input
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="6-digit code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full rounded-xl border border-slate-300 px-4 py-3 text-center text-xl font-mono tracking-widest focus:outline-none focus:ring-2 focus:ring-sky-500"
                required
              />
              {error && <p className="text-sm text-rose-600">{error}</p>}
              <button
                type="submit"
                disabled={saving || code.length < 6}
                className="w-full rounded-full bg-sky-600 py-3 font-semibold text-white transition hover:bg-sky-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Enable 2FA'}
              </button>
            </form>
          </>
        ) : (
          <p className="text-sm text-slate-400">Generating QR code…</p>
        )}
      </div>
    </div>
  );
}
